import { useT } from '@transifex/react';
import { useEffect, useReducer } from 'react';

import DateTime from 'pkg/datetime';

import InfoBox from 'components/form/info-box';

export function isCheckoutLocked(
	lockedUntil?: number | null
): lockedUntil is number {
	return !!lockedUntil && lockedUntil * 1000 > DateTime.now().getTimestamp();
}

export function formatLockTime(lockedUntil: number): string {
	return DateTime.fromTimestamp(lockedUntil).toTimeString();
}

interface CheckoutLockNoticeProps {
	scope: 'order' | 'subscription';
	lockedUntil?: number | null;
	// Allowed actions are computed with the lock, so the page refetches to show the actions it hid.
	onExpire?: () => void;
}

export function CheckoutLockNotice({
	scope,
	lockedUntil,
	onExpire,
}: CheckoutLockNoticeProps) {
	const t = useT();
	const [, rerender] = useReducer((n: number) => n + 1, 0);

	// One second past the lock end is margin for clock skew and latency, so the refetch reads the lock as over.
	useEffect(() => {
		if (!isCheckoutLocked(lockedUntil)) {
			return;
		}

		const timeout = setTimeout(
			() => {
				rerender();
				onExpire?.();
			},
			(lockedUntil + 1) * 1000 - DateTime.now().getTimestamp()
		);

		return () => clearTimeout(timeout);
	}, [lockedUntil, onExpire]);

	if (!isCheckoutLocked(lockedUntil)) {
		return null;
	}

	const time = formatLockTime(lockedUntil);

	let text: string;

	if (scope === 'order') {
		text = t(
			'Someone is paying this order right now. It is locked until {time}.',
			{ time }
		);
	} else {
		text = t(
			'Someone is paying an order on this subscription right now. It is locked until {time}.',
			{ time }
		);
	}

	return <InfoBox color="orange">{text}</InfoBox>;
}
